import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, Trash2, CheckCircle } from "lucide-react";
import * as XLSX from "xlsx-js-style";
import axios from "axios";
import { useModal } from "../../hooks/useModal";
import { Modal } from "../ui/modal";
import LogButton from "../common/LogButton";
import { DownArrow, PlusIcon, UpArrow } from "../svg/Icons";
import RowsPerPageSelector from "../common/RowsPerPageSelector";
import Pagination from "../common/Pagination";
import { styles } from "../../styles/pages/Quotation/QuotesTableStyles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faTrash,
  faEye,
  faDownload,
  faEnvelope,
} from "@fortawesome/free-solid-svg-icons";
import Loader from "../common/Loader";
import generateQuotationPDF from "../../pages/Quotation/generateQuotationPDF";
import { ReactComponent as DownloadIcon } from "../../icons/ExcelDownloadIcon.svg";
import { Tooltip } from "../../components/common/Tooltip";

interface APIQuotation {
  id: string | number;
  quotation_number: string;
  group_total: string;
  quotation_type: string;
  quotation_status: string;
  opportunity: string;
  valid_until: string;
  products?: any[];
  services?: any[];
  log: string;
  quotation_subject?: string;
  from_company_name?: string;
  from_address?: string;
  from_phone?: string;
  from_email?: string;
  to_client_name?: string;
  to_address?: string;
  to_email?: string;
  terms_and_conditions?: string;
}

export interface Quotation {
  id: string | number;
  quotation_number: string;
  group_total: string;
  quotation_type: string;
  quotation_status: string;
  opportunity: string;
  valid_until: string;
  log: string;
  quotation_subject?: string;
  from_company_name?: string;
  from_address?: string;
  from_phone?: string;
  from_email?: string;
  to_client_name?: string;
  to_address?: string;
  to_email?: string;
  terms_and_conditions?: string;
}

export interface ProductDetails {
  id: number;
  name: string;
  description: string;
  price: string;
  quantity: number;
  CGST?: string;
  SGST?: string;
  IGST?: string;
  discount?: string;
  discountType?: number;
  type?: string;
  status?: number; // Added status field
}

export interface ServiceDetails {
  id: number;
  title: string;
  description: string;
  duration: string;
  cost: string;
  service_GST?: string;
  discount?: string;
  discountType?: number;
  type?: string;
}

const QuotesTable: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  // const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [quotations, setQuotations] = useState<APIQuotation[]>([]);
  const [sortColumn, setSortColumn] = useState<keyof Quotation | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { isOpen, openModal, closeModal } = useModal();
  const [selectedQuotationId, setSelectedQuotationId] = useState<
    string | number | null
  >(null);
  const [isDeleteSuccessModalOpen, setDeleteSuccessModalOpen] = useState(false);
  const [, setViewModalOpen] = useState(false);
  const [, setViewDetails] = useState<(ProductDetails | ServiceDetails)[]>([]);
  const [, setViewType] = useState<"Product" | "Service" | null>(null);
  const [, setViewLoading] = useState(false);
  const [, setSelectedQuotation] = useState<Quotation | null>(null);
  const [downloadLoading, setDownloadLoading] = useState<{
    [key: string]: boolean;
  }>({});
  const [emailLoading, setEmailLoading] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [isEmailSuccessModalOpen, setEmailSuccessModalOpen] = useState(false);

  const getAuthToken = (): string | null => {
    const token = sessionStorage.getItem("access_token");
    if (!token) {
      console.error("No auth token found.");
    }
    return token;
  };

  const axiosConfig = (token: string) => ({
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "multipart/form-data",
    },
  });

  const fetchQuotations = useCallback(async () => {
    setLoading(true);
    setError(null);

    const token = getAuthToken();
    if (!token) {
      setError("Authentication token not found. Please log in.");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/quotation/search/list`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.status === 200) {
        const formattedQuotations: Quotation[] = await Promise.all(
          response.data.data.map(async (item: any) => {
            let recalculatedGroupTotal = 0;

            const isProduct = item.quotation_type === "Product" || item.quotation_type === 1 || item.quotation_type === "1";

            if (isProduct && Array.isArray(item.products)) {
              item.products
                .filter((p: any) => p.status !== 0)
                .forEach((p: any) => {
                  const price = parseFloat(p.price || "0");
                  const qty = parseInt(p.quantity || "1", 10);
                  const discountStr = String(p.discount || "0").trim();
                  const discountType = p.discount_type || 1;
                  const discountValue = parseFloat(discountStr.replace(/[^0-9.]/g, "")) || 0;

                  const subtotal = price * qty;
                  const discount = discountType === 1 ? subtotal * (discountValue / 100) : discountValue;
                  const taxable = subtotal - discount;

                  const CGST = parseFloat(p.CGST || "0");
                  const SGST = parseFloat(p.SGST || "0");
                  const IGST = parseFloat(p.IGST || "0");
                  const gst = IGST > 0 ? taxable * (IGST / 100) : taxable * ((CGST + SGST) / 100);

                  recalculatedGroupTotal += taxable + gst;
                });
            } else if (!isProduct && Array.isArray(item.services)) {
              item.services.forEach((s: any) => {
                const cost = parseFloat(s.cost || s.price || "0");
                const discountStr = String(s.discount || "0").trim();
                const discountType = s.discount_type || 1;
                const discountValue = parseFloat(discountStr.replace(/[^0-9.]/g, "")) || 0;

                const subtotal = cost;
                const discount = discountType === 1 ? subtotal * (discountValue / 100) : discountValue;
                const taxable = subtotal - discount;
                const gstRate = parseFloat(s.service_GST || "0");
                const gst = taxable * (gstRate / 100);

                recalculatedGroupTotal += taxable + gst;
              });
            }

            return {
              id: item.id,
              quotation_number: item.quotation_number,
              group_total: recalculatedGroupTotal.toFixed(2), // ← NOW CORRECT
              quotation_type: item.quotation_type,
              quotation_status: item.quotation_status,
              opportunity: item.opportunity,
              valid_until: item.valid_until,
              log: item.log || "",
              quotation_subject: item.quotation_subject || "",
              from_company_name: item.from_company_name || "N/A",
              from_address: item.from_address || "N/A",
              from_phone: item.from_phone || "N/A",
              from_email: item.from_email || "N/A",
              to_client_name: item.to_client_name || "N/A",
              to_address: item.to_address || "N/A",
              to_email: item.to_email || "N/A",
              terms_and_conditions: item.terms_and_conditions || "N/A",
            };
          })
        );
        const uniqueQuotations = formattedQuotations.filter(
  (q, index, self) => index === self.findIndex((t) => t.id === q.id)
);
setQuotations(uniqueQuotations);
      } else {
        setQuotations([]);
        setError("No quotations found.");
      }
    } catch (err) {
      console.error("Quotations API Error:", err);
      setError(
        axios.isAxiosError(err) && err.response
          ? err.response.data.message || "Failed to fetch quotations."
          : "An unknown error occurred."
      );
      setQuotations([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDetails = async (
    quotationNumber: string,
    isForPDF: boolean = false
  ): Promise<{
    quotation: Quotation;
    details: (ProductDetails | ServiceDetails)[];
    viewType: "Product" | "Service" | null;
    recalculatedGroupTotal?: string; // Added to return recalculated total
  } | null> => {
    if (!quotationNumber) {
      console.error("Invalid quotationNumber:", quotationNumber);
      alert("Invalid quotation number.");
      if (!isForPDF) setViewLoading(false);
      return null;
    }

    if (!isForPDF) setViewLoading(true);

    const token = getAuthToken();
    if (!token) {
      if (!isForPDF) setViewLoading(false);
      alert("Authentication token not found.");
      return null;
    }
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/quotation/search/list`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.status === 200 && Array.isArray(response.data.data)) {
        const data = response.data.data.find(
          (item: any) =>
            String(item.quotation_number).trim() ===
            String(quotationNumber).trim()
        );

        if (data) {
          const selectedData: Quotation = {
            id: data.id,
            quotation_number: data.quotation_number,
            group_total: data.group_total, // Will be overridden in PDF/email if recalculated
            quotation_type: data.quotation_type,
            quotation_status: data.quotation_status,
            opportunity: data.opportunity,
            valid_until: data.valid_until,
            log: data.log || "",
            quotation_subject: data.quotation_subject || "",
            from_company_name: data.from_company_name || "N/A",
            from_address: data.from_address || "N/A",
            from_phone: data.from_phone || "N/A",
            from_email: data.from_email || "N/A",
            to_client_name: data.to_client_name || "N/A",
            to_address: data.to_address || "N/A",
            to_email: data.to_email || "N/A",
            terms_and_conditions: data.terms_and_conditions || "N/A",
          };

          const isProduct: boolean =
            data.quotation_type === "Product" ||
            data.quotation_type === 1 ||
            data.quotation_type === "1";

          let details: (ProductDetails | ServiceDetails)[] = [];
          let recalculatedGroupTotal: number = 0;

          if (isProduct && Array.isArray(data.products)) {
            details = data.products
              .filter((product: any) => product.status !== 0) // Filter out inactive products
              .map((product: any) => {
                const CGST = parseFloat(product.CGST || "0");
                const SGST = parseFloat(product.SGST || "0");
                const IGST = parseFloat(product.IGST || "0");
                const price = parseFloat(product.price || "0");
                const quantity = parseInt(product.quantity || "1", 10);
                const discountStr = String(product.discount || "0").trim();
                const discountType = product.discount_type || 1;
                const discountValue = parseFloat(discountStr.replace(/[^0-9.]/g, "")) || 0;

                // Calculate product total for recalculation
                const subtotal = price * quantity;
                let discount = discountType === 1 ? subtotal * (discountValue / 100) : discountValue;
                const taxableAmount = subtotal - discount;
                const cgstAmount = taxableAmount * (CGST / 100);
                const sgstAmount = taxableAmount * (SGST / 100);
                const igstAmount = taxableAmount * (IGST / 100);
                const totalGSTAmount = IGST > 0 ? igstAmount : cgstAmount + sgstAmount;
                const total = taxableAmount + totalGSTAmount;

                recalculatedGroupTotal += total;

                return {
                  id: product.id || 0,
                  name: product.product_name || product.name || "Unknown Product",
                  description: product.description || "No description available",
                  price: String(product.price || "0"),
                  quantity,
                  CGST: String(CGST),
                  SGST: String(SGST),
                  IGST: String(IGST),
                  discount: discountStr,
                  discountType,
                  type: product.type || "Standard",
                  status: product.status || 1, // Include status field
                };
              });
          } else if (!isProduct && Array.isArray(data.services)) {
            details = data.services.map((service: any) => {
              const price = parseFloat(service.cost || service.price || "0");
              const discountStr = String(service.discount || "0").trim();
              const discountType = service.discount_type || 1;
              const discountValue = parseFloat(discountStr.replace(/[^0-9.]/g, "")) || 0;
              const gstRate = parseFloat(service.service_GST || "0");

              // Calculate service total for recalculation
              const subtotal = price;
              let discount = discountType === 1 ? subtotal * (discountValue / 100) : discountValue;
              const taxableAmount = subtotal - discount;
              const gstAmount = taxableAmount * (gstRate / 100);
              const total = taxableAmount + gstAmount;

              recalculatedGroupTotal += total;

              return {
                id: service.id || 0,
                title: service.service_name || service.title || "Unknown Service",
                description: service.description || "No description available",
                duration: service.duration || "N/A",
                cost: String(service.cost || service.price || "0"),
                service_GST: String(service.service_GST || "0"),
                discount: discountStr,
                discountType,
                type: service.type || "Standard",
              };
            });
          } else {
            console.warn(
              "No product or service data found for this quotation."
            );
            details = [];
          }

          if (isForPDF) {
            return {
              quotation: selectedData,
              details,
              viewType: isProduct ? "Product" : "Service",
              recalculatedGroupTotal: recalculatedGroupTotal.toFixed(2), // Return formatted total
            };
          } else {
            setSelectedQuotation(selectedData);
            setViewType(isProduct ? "Product" : "Service");
            setViewDetails(details);
            setViewModalOpen(true);
          }
        } else {
          console.warn(
            "Detailed data not found for quotation number:",
            quotationNumber
          );
          if (!isForPDF) setViewDetails([]);
          alert("No details found for this quotation.");
        }
      } else {
        console.warn("API returned no valid data.");
        if (!isForPDF) setViewDetails([]);
        alert("No valid data found for this quotation.");
      }
    } catch (err) {
      console.error("Details fetch Error:", err);
      if (!isForPDF) setViewDetails([]);
      alert(
        axios.isAxiosError(err) && err.response
          ? err.response.data.message || "Failed to fetch quotation details."
          : "An unexpected error occurred."
      );
    } finally {
      if (!isForPDF) setViewLoading(false);
    }
    return null;
  };

  const handleConfirmDelete = async () => {
    if (!selectedQuotationId) return;

    const token = getAuthToken();
    if (!token) return;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/quotation/delete`,
        { quotation_id: selectedQuotationId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.status === 200) {
        setQuotations((prevQuotations) => {
          const updatedQuotations = prevQuotations.filter(
            (quotation) => quotation.id !== selectedQuotationId
          );
          if (
            updatedQuotations.length > 0 &&
            displayedQuotations.length === 1
          ) {
            setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
          }
          return updatedQuotations;
        });
        closeModal();
        setDeleteSuccessModalOpen(true);
        setTimeout(() => setDeleteSuccessModalOpen(false), 2000);
      }
    } catch (err) {
      console.error("Error deleting quotation:", err);
      alert(
        axios.isAxiosError(err) && err.response
          ? err.response.data.message || "Failed to delete quotation."
          : "An unexpected error occurred."
      );
    }
  };

  const handleSendEmail = async (
    quotationNumber: string,
    quotationId: string | number
  ) => {
    setEmailLoading((prev) => ({ ...prev, [quotationId]: true }));
    try {
      const data = await fetchDetails(quotationNumber, true);
      if (!data) {
        alert("Failed to fetch quotation details for email.");
        return;
      }

      const formattedQuotation = {
        quotation_number: data.quotation.quotation_number,
        quotation_subject: data.quotation.quotation_subject || "N/A",
        quotation_type: data.quotation.quotation_type || "N/A",
        valid_until: data.quotation.valid_until || "N/A",
        group_total: data.recalculatedGroupTotal || data.quotation.group_total, // Use recalculated total
        from_company_name: data.quotation.from_company_name || "N/A",
        from_address: data.quotation.from_address || "N/A",
        from_phone: data.quotation.from_phone || "N/A",
        from_email: data.quotation.from_email || "N/A",
        to_client_name: data.quotation.to_client_name || "N/A",
        to_address: data.quotation.to_address || "N/A",
        to_email: data.quotation.to_email || "N/A",
        terms_and_conditions: data.quotation.terms_and_conditions || "N/A",
      };

      const pdfBlob = await generateQuotationPDF({
        quotation: formattedQuotation,
        details: data.details,
        viewType: data.viewType,
      });

      const token = getAuthToken();
      if (!token) {
        alert("Authentication token not found.");
        return;
      }

      const pdfFile = new File([pdfBlob], `Quotation_${quotationNumber}.pdf`, {
        type: "application/pdf",
      });

      const formData = new FormData();
      formData.append("pdf", pdfFile);
      formData.append("email", formattedQuotation.to_email);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/send-quotation`,
        formData,
        axiosConfig(token)
      );

      if (response.status === 200) {
        setEmailSuccessModalOpen(true);
      } else {
        alert("Failed to send quotation email.");
      }
    } catch (error) {
      console.error("Email Send Error:", error);
      alert("An error occurred while sending the email. Please try again.");
    } finally {
      setEmailLoading((prev) => ({ ...prev, [quotationId]: false }));
    }
  };

  const filteredQuotations = quotations.filter((quotation) =>
    Object.values(quotation)
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const sortedQuotations = [...filteredQuotations].sort((a, b) => {
    if (!sortColumn) return 0;
    const valA = a[sortColumn];
    const valB = b[sortColumn];
    return typeof valA === "string" && typeof valB === "string"
      ? sortOrder === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA)
      : sortOrder === "asc"
        ? Number(valA) - Number(valB)
        : Number(valB) - Number(valA);
  });

const [isTabletView, setIsTabletView] = useState(window.innerWidth < 768);

useEffect(() => {
  const handleResize = () => setIsTabletView(window.innerWidth < 768);
  window.addEventListener("resize", handleResize);
  return () => window.removeEventListener("resize", handleResize);
}, []);

  const displayedQuotations = isTabletView
    ? sortedQuotations
    : sortedQuotations.slice(
      (currentPage - 1) * rowsPerPage,
      currentPage * rowsPerPage
    );
  const totalPages = isTabletView
    ? 1
    : Math.ceil(filteredQuotations.length / rowsPerPage);

  const handleDeleteClick = (quotationId: string | number) => {
    setSelectedQuotationId(quotationId);
    openModal();
  };

  const handlePageChange = (newPage: number) => setCurrentPage(newPage);

  const handleSort = (column: keyof Quotation) => {
    setSortColumn(column);
    setSortOrder(sortColumn === column && sortOrder === "asc" ? "desc" : "asc");
  };

  const handleDownload = () => {
    const dataToDownload = searchTerm
      ? filteredQuotations
      : sortColumn
        ? sortedQuotations
        : quotations;

    if (!dataToDownload.length) {
      alert("No data available to download.");
      return;
    }

    const formattedData = dataToDownload.map((quotation) => ({
      "Quotation Number": isDateExpired(quotation.valid_until)
        ? `${quotation.quotation_number} (Expired)`
        : quotation.quotation_number,
      "Quotation Type": quotation.quotation_type,
      "Quotation Status": quotation.quotation_status,
      Opportunity: quotation.opportunity,
      "Valid Till": quotation.valid_until,
      "Group Total": new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 2,
      }).format(Number(quotation.group_total)),
    }));

    const headers = Object.keys(formattedData[0]);
    const worksheet = XLSX.utils.aoa_to_sheet([]);

    XLSX.utils.sheet_add_aoa(worksheet, [["Quotation List"]], { origin: "A1" });

    worksheet["!merges"] = [
      {
        s: { r: 0, c: 0 },
        e: { r: 0, c: headers.length - 1 },
      },
    ];

    worksheet["A1"].s = {
      font: { bold: true, sz: 14 },
      alignment: { horizontal: "center", vertical: "center" },
    };

    XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: "A2" });

    XLSX.utils.sheet_add_json(worksheet, formattedData, {
      origin: "A3",
      skipHeader: true,
    });

    headers.forEach((_, index) => {
      const col = String.fromCharCode(65 + index);
      const cell = `${col}2`;
      if (worksheet[cell]) {
        worksheet[cell].s = {
          font: { bold: true },
        };
      }
    });

    formattedData.forEach((row, rowIndex) => {
      if (row["Quotation Number"].includes("(Expired)")) {
        const cell = `A${rowIndex + 3}`;
        worksheet[cell].s = {
          font: { italic: true },
        };
      }
    });

    worksheet["!cols"] = headers.map((key) => ({
      wch:
        Math.max(
          key.length,
          ...formattedData.map(
            (row) => String(row[key as keyof typeof row])?.length || 0
          ),
          "Quotation List".length
        ) + 2,
    }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Quotations");
    XLSX.writeFile(
      workbook,
      searchTerm ? `QuotationData_${searchTerm}.xlsx` : "QuotationData.xlsx"
    );
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage, isTabletView]);

  useEffect(() => {
    fetchQuotations();
  }, [fetchQuotations]);
  

  const isDateExpired = (validUntil: string): boolean => {
    const [day, month, year] = validUntil.split("-").map(Number);
    const validDate = new Date(year, month - 1, day);
    const today = new Date();
    if (isNaN(validDate.getTime())) {
      console.error(`Invalid date format for validUntil: ${validUntil}`);
      return false;
    }

    validDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    return validDate < today;
  };

  const handleDownloadPDF = async (
    quotationNumber: string,
    quotationId: string | number,
    fetchDetails: any
  ) => {
    setDownloadLoading((prev) => ({ ...prev, [quotationId]: true }));
    try {
      const data = await fetchDetails(quotationNumber, true);
      if (data) {
        const formattedQuotation = {
          quotation_number: data.quotation.quotation_number,
          quotation_subject: data.quotation.quotation_subject || "",
          quotation_type: data.quotation.quotation_type,
          valid_until: data.quotation.valid_until,
          group_total: data.recalculatedGroupTotal || data.quotation.group_total, // Use recalculated total
          from_company_name: data.quotation.from_company_name || "StarOneWeb",
          from_address:
            data.quotation.from_address ||
            "5, Shubh Business Park, Nr. Chhadi Water works, Gandhinagar - Mahudi Road, Gandhinagar, Gujarat",
          from_phone: data.quotation.from_phone || "7642069858",
          from_email: data.quotation.from_email || "contact@staroneweb.com",
          to_client_name: data.quotation.to_client_name || "Hem Vaghla",
          to_address: data.quotation.to_address || "delay@gmail.com",
          to_email: data.quotation.to_email || "delay@gmail.com",
          terms_and_conditions: data.quotation.terms_and_conditions || "",
        };

        const pdfBlob = await generateQuotationPDF({
          quotation: formattedQuotation,
          details: data.details,
          viewType: data.viewType,
        });

        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Quotation_${quotationNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        alert("Failed to fetch quotation details for PDF generation.");
      }
    } catch (error) {
      console.error("PDF Download Error:", error);
      alert("An error occurred while generating the PDF. Please try again.");
    } finally {
      setDownloadLoading((prev) => ({ ...prev, [quotationId]: false }));
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>Quotation List</h3>
      </div>

      <div className={styles.tableCard}>
        <div className={styles.headerControls}>
          <RowsPerPageSelector
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
          />
          <div className={styles.controlsInner}>
            <div className={styles.searchContainer}>
              <Search className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search..."
                className={styles.searchInput}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button className={styles.downloadButton} onClick={handleDownload}>
              Download
              <DownloadIcon className={styles.downloadIcon} />
            </button>
            <Link to="/quotation-add" className={styles.addButtonContainer}>
              <button className={styles.addButton}>
                Add New Quotation
                <PlusIcon />
              </button>
            </Link>
          </div>
        </div>
        {error && <p className={styles.errorText}>{error}</p>}

        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr className={styles.theadRow}>
                <th
                  className={styles.thName}
                  onClick={() => handleSort("quotation_number")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Number</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th
                  className={styles.thTexts}
                  onClick={() => handleSort("quotation_type")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Type</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th
                  className={styles.thTexts}
                  onClick={() => handleSort("quotation_status")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Status</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th
                  className={styles.thTexts}
                  onClick={() => handleSort("opportunity")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Opportunity</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th
                  className={styles.thTexts}
                  onClick={() => handleSort("valid_until")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Valid Till</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th
                  className={styles.thName}
                  onClick={() => handleSort("group_total")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Group Total</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th className={styles.thAction}>
                  <p className={styles.thText}>Action</p>
                </th>
                <th className={styles.thLog}>
                  <p className={styles.thText}>Log</p>
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-4">
                    <Loader />
                  </td>
                </tr>
              ) : displayedQuotations.length > 0 ? (
                displayedQuotations.map((quotation) => {
                  const expired = isDateExpired(quotation.valid_until);
                  return (
                    <tr key={String(quotation.id)} className={styles.tbodyRow}>
                      <td
                        className={`${styles.tdName} ${expired ? styles.expiredText : ""
                          }`}
                      >
                        {quotation.quotation_number}
                        {expired && (
                          <span className="italic ml-1">(Expired)</span>
                        )}
                      </td>
                      <td
                        className={`${styles.tdName} ${expired ? styles.expiredText : ""
                          }`}
                      >
                        {quotation.quotation_type}
                      </td>
                      <td
                        className={`${styles.tdName} ${expired ? styles.expiredText : ""
                          }`}
                      >
                        {quotation.quotation_status}
                      </td>
                      <td
                        className={`${styles.tdName} ${expired ? styles.expiredText : ""
                          }`}
                      >
                        {quotation.opportunity}
                      </td>
                      <td
                        className={`${styles.tdName} ${expired ? styles.expiredText : ""
                          }`}
                      >
                        {quotation.valid_until}
                      </td>
                      <td
                        className={`${styles.tdName} ${expired ? styles.expiredText : ""
                          }`}
                      >
                        ₹
                        {parseFloat(quotation.group_total).toLocaleString(
                          "en-IN",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }
                        )}
                      </td>
                      <td className={styles.tdAction}>
                        <div className={styles.actionContainer}>
                          <Tooltip content="View" bgColor="bg-green-600">
                            <Link
                              to="/quotation-view"
                              state={{
                                quotationNumber: quotation.quotation_number,
                              }}
                              className="inline-flex"
                            >
                              <button className={styles.viewButton}>
                                <FontAwesomeIcon
                                  icon={faEye}
                                  className="text-white text-sm"
                                />
                              </button>
                            </Link>
                          </Tooltip>
                          {!expired && (
                            <Tooltip content="Edit" bgColor="bg-[#2f6aa7]">
                              <Link
                                to={`/quotation-edit/${quotation.id}`}
                                className="inline-flex"
                              >
                                <button className={styles.editButton}>
                                  <FontAwesomeIcon
                                    icon={faEdit}
                                    className="text-white text-sm"
                                  />
                                </button>
                              </Link>
                            </Tooltip>
                          )}
                          <Tooltip content="Delete" bgColor="bg-red-600">
                            <button
                              className={styles.deleteButton}
                              onClick={() => handleDeleteClick(quotation.id)}
                            >
                              <FontAwesomeIcon
                                icon={faTrash}
                                className="text-white text-sm"
                              />
                            </button>
                          </Tooltip>
                          <Tooltip content="Download" bgColor="bg-[#d2a81b]">
                            <button
                              className={styles.downloadsButton}
                              onClick={() =>
                                handleDownloadPDF(
                                  quotation.quotation_number,
                                  quotation.id,
                                  fetchDetails
                                )
                              }
                              disabled={downloadLoading[quotation.id]}
                            >
                              {downloadLoading[quotation.id] ? (
                                <svg
                                  className="animate-spin h-5 w-5 text-white"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8v8h8a8 8 0 01-16 0z"
                                  />
                                </svg>
                              ) : (
                                <FontAwesomeIcon
                                  icon={faDownload}
                                  className="text-white text-sm"
                                />
                              )}
                            </button>
                          </Tooltip>
                          <Tooltip content="Mail" bgColor="bg-[#167968]">
                            <button
                              className={styles.emailButton}
                              onClick={() =>
                                handleSendEmail(
                                  quotation.quotation_number,
                                  quotation.id
                                )
                              }
                              disabled={emailLoading[quotation.id]}
                            >
                              {emailLoading[quotation.id] ? (
                                <svg
                                  className="animate-spin h-5 w-5 text-white"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8v8h8a8 8 0 01-16 0z"
                                  />
                                </svg>
                              ) : (
                                <FontAwesomeIcon
                                  icon={faEnvelope}
                                  className="text-white text-sm"
                                />
                              )}
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                      <td className={styles.td}>
                        <LogButton log={quotation.log} />
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={8} className={styles.noDataText}>
                    No records match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {!isTabletView && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            filteredLength={filteredQuotations.length}
            handlePageChange={handlePageChange}
          />
        )}
      </div>

      <Modal
        isOpen={isOpen}
        onClose={closeModal}
        className="w-[90%] max-w-[400px] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 relative transform transition-all duration-300 ease-in-out"
      >
        <div className="flex justify-center mb-4">
          <div className="w-14 h-14 flex items-center justify-center bg-red-100 text-red-600 rounded-full">
            <Trash2 className="w-6 h-6" />
          </div>
        </div>
        <h4 className="text-xl font-bold text-gray-800 dark:text-white mb-3 text-center">
          Confirm Delete
        </h4>
        <p className="text-gray-600 dark:text-gray-400 mb-6 text-center">
          Are you sure you want to delete this quotation? This action cannot be
          undone.
        </p>
        <div className="flex justify-center gap-4">
          <button
            onClick={closeModal}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmDelete}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
          >
            Delete
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={isDeleteSuccessModalOpen}
        onClose={() => setDeleteSuccessModalOpen(false)}
        className="w-[90%] max-w-[400px] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 relative transform transition-all duration-300 ease-in-out"
      >
        <div className="flex justify-center mb-4">
          <div className="w-14 h-14 flex items-center justify-center bg-green-100 text-green-600 rounded-full">
            <CheckCircle className="w-6 h-6" />
          </div>
        </div>
        <h4 className="text-xl font-bold text-gray-800 dark:text-white mb-3 text-center">
          Quotation Deleted Successfully!
        </h4>
        <p className="text-gray-600 dark:text-gray-400 mb-6 text-center">
          The quotation has been removed from the system.
        </p>
        <div className="flex justify-center">
          <button
            onClick={() => setDeleteSuccessModalOpen(false)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
          >
            OK
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={isEmailSuccessModalOpen}
        onClose={() => setEmailSuccessModalOpen(false)}
        className="w-[90%] max-w-[400px] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 relative transform transition-all duration-300 ease-in-out"
      >
        <div className="flex justify-center mb-4">
          <div className="w-14 h-14 flex items-center justify-center bg-green-100 text-green-600 rounded-full">
            <CheckCircle className="w-6 h-6" />
          </div>
        </div>
        <h4 className="text-xl font-bold text-gray-800 dark:text-white mb-3 text-center">
          Email Sent Successfully!
        </h4>
        <p className="text-gray-600 dark:text-gray-400 mb-6 text-center">
          The quotation has been sent to the client's email.
        </p>
        <div className="flex justify-center">
          <button
            onClick={() => setEmailSuccessModalOpen(false)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
          >
            OK
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default QuotesTable;